import { z } from 'zod'
import {
  learningProgramPublicationSchema,
} from './learning-publications'
import { learningEnrollmentSchema } from './learning-enrollments'
import { learningProgramAchievementSchema, learningProgramDefinitionSchema } from './learning-program-definition'

// Learner SPA wire (LMSEngine ProgramOverview): version.id is the program version id
// sent as launch context, version.publishedAt is displayed; enrollment.id mirrors enrollmentId.
const learningLearnerProgramVersionSchema = learningProgramPublicationSchema.extend({
  id: z.string().trim().min(1),
  publishedAt: z.string().datetime({ offset: true }),
  definition: learningProgramDefinitionSchema,
}).strict()
const learningLearnerEnrollmentSchema = learningEnrollmentSchema.extend({
  id: z.string().trim().min(1),
}).strict()

export const LEARNING_LEARNER_PROGRAMS_CONTRACT_VERSION = '1.0.0' as const
export const LEARNING_LEARNER_PROGRAMS_SCHEMA_VERSION = 'learning-learner-programs-v1' as const

const text = z.string().trim().min(1)
const timestamp = z.string().datetime({ offset: true })

export const learningLearnerRequirementProjectionSchema = z.object({
  id: text.max(180),
  title: text.max(240),
  courseSlug: text.max(180),
  artifactId: z.string().trim().max(180),
  catalogNodeId: text.max(160),
  required: z.boolean(),
  requiredOutcome: z.enum(['completed', 'passed']),
  acceptedEvidenceStandards: z.array(text.max(160)).max(50),
  evidenceStandard: z.string().trim().max(160),
  freshnessMaxAgeDays: z.number().int().positive().nullable(),
  status: z.enum(['satisfied', 'unsatisfied']),
  credited: z.boolean(),
  courseCompleted: z.boolean(),
  coursePassed: z.boolean(),
  evidenceAchievementId: z.string().uuid().nullable(),
  achievementId: z.string().uuid().nullable(),
  canAccess: z.boolean(),
  accessReason: z.enum(['ENTITLEMENT_REQUIRED', 'CONTENT_UNAVAILABLE']).nullable(),
  blockedReason: z.string().trim().max(240).nullable(),
}).strict()

export const learningLearnerProgramProjectionSchema = z.object({
  earnedCredits: z.number().int().nonnegative(),
  requiredCredits: z.number().int().nonnegative(),
  status: z.enum(['not_started', 'in_progress', 'completed']),
  requirements: z.array(learningLearnerRequirementProjectionSchema),
  nextRequirementId: text.max(180).nullable(),
  completedAt: timestamp.nullable(),
}).strict()

export const learningLearnerProgramDetailSchema = z.object({
  version: learningLearnerProgramVersionSchema,
  enrollment: learningLearnerEnrollmentSchema.nullable(),
  projection: learningLearnerProgramProjectionSchema,
  achievement: learningProgramAchievementSchema.nullable(),
  access: z.object({ canEnroll: z.boolean() }).strict(),
}).strict()

export const learningLearnerProgramDetailResponseSchema = learningLearnerProgramDetailSchema
export const learningLearnerProgramStartResponseSchema = learningLearnerProgramDetailSchema

export type LearningLearnerRequirementProjection = z.infer<typeof learningLearnerRequirementProjectionSchema>
export type LearningLearnerProgramProjection = z.infer<typeof learningLearnerProgramProjectionSchema>
export type LearningLearnerProgramDetail = z.infer<typeof learningLearnerProgramDetailSchema>
export type LearningLearnerProgramDetailResponse = LearningLearnerProgramDetail
export type LearningLearnerProgramStartResponse = LearningLearnerProgramDetail
